import ModelIcon from '../ModelIcon.tsx'
import { ModelShields } from '../RestrictionShields.tsx'
import HealthDot from '../HealthDot.tsx'
import { Coin } from '../../icons/Coin.tsx'
import { Star } from '../../icons/Star.tsx'
import { User } from '../../icons/User.tsx'
import { useProviderHealth, type ProviderHealthStatus } from '../../hooks/useProviderHealth.ts'
import { isDynamicPriced } from '../../pages/chat/components/ContextButton.tsx'

import type { Model } from '../../data/models.ts'

type ModelCardVariant = 'bots' | 'selector'


// Цвет и подпись индикатора работоспособности нейросети.
const HEALTH_UI: Record<ProviderHealthStatus, { color: string; label: string }> = {
	healthy: { color: '#22c55e', label: 'Стабильна и доступна' },
	degraded: { color: '#eab308', label: 'Отвечает с задержкой или единичными сбоями' },
	down: { color: '#ef4444', label: 'Не отвечает — Сбой на стороне провайдера' },
	unknown: { color: '#9ca3af', label: 'Статус неизвестен' },
}

interface ModelCardProps {
	model: Model
	onClick: (model: Model) => void
	variant?: ModelCardVariant
	isActive?: boolean
	showRadio?: boolean
}

// Карточка показывает min ценник (из minCreditsPrice), а /generate
// считает по дефолтным settings — для моделей с разноценовыми вариантами
// числа расходятся. Префикс «от» честно сигнализирует пользователю, что
// видимое значение — это минимум, а итог зависит от настроек.
function hasMultiVariantPricing(model: Model): boolean {
	const variants = model.variants
	if (!variants || variants.length < 2) return false
	const prices = variants
		.map((v) => v.credits_price)
		.filter((p): p is number => typeof p === 'number')
	if (prices.length < 2) return false
	return Math.min(...prices) !== Math.max(...prices)
}

export default function ModelCard({
	model,
	onClick,
	variant = 'bots',
	isActive = false,
	showRadio = false,
}: ModelCardProps) {
	const rootClassName = ['model-item', variant === 'selector' && isActive ? 'active' : '']
		.filter(Boolean)
		.join(' ')

	// «от» на ценнике: (а) у модели разноценовые варианты — показан минимум;
	// (б) Pro/reasoning-модель с динамическим списанием по факту токенов
	// (isDynamicPriced) — итог обычно выше базовой цены. Требование 31.07.2026:
	// «от» видно уже на карточках («Все нейросети» и верхний дропдаун), а не
	// только в поповере памяти чата.
	const showFromPrefix = model.cost > 0 && (hasMultiVariantPricing(model) || isDynamicPriced(model.id))

	// Карточка-инструмент (catalog_tools): у неё нет провайдера, цены и
	// синтетических рейтингов — рисуем подпись цены как есть и бейдж «Инструмент».
	const isTool = model.isTool === true



	const health = useProviderHealth()
	const healthEntry = health[model.provider]
	// Статус шлюза (пинг/баланс) — общий для всех моделей провайдера.
	const gatewayStatus: ProviderHealthStatus =
		(healthEntry?.status as ProviderHealthStatus) || 'unknown'
	// Адресные 5xx-ошибки по КОНКРЕТНОЙ модели (req 2): красный вешаем только
	// на ту модель, по которой реально были сбои, а не на весь провайдер.
	const modelErrorCount = healthEntry?.model_errors?.[model.id] || 0
	const status: ProviderHealthStatus = modelErrorCount > 0 ? 'down' : gatewayStatus
	const healthUi = HEALTH_UI[status]
	// Для тултипа: счётчик ошибок берём модельный, иначе — суммарный по шлюзу.
	const errCount = modelErrorCount > 0 ? modelErrorCount : healthEntry?.recent_errors || 0
	const healthTitle =
		status === 'down' && errCount > 0
			? `${model.name}: ${healthUi.label} (ошибок: ${errCount})`
			: `${model.name}: ${healthUi.label}`

	return (
		<button type="button" className={rootClassName} onClick={() => onClick(model)}>
			<span className="model-cost">
				{isTool ? (
					model.priceLabel ? <>{model.priceLabel}</> : null
				) : model.cost > 0 ? (
					<>
						{showFromPrefix && <span className="model-cost-from">от</span>}
						{model.cost}
						<Coin size={variant === 'selector' ? 12 : undefined} />
					</>
				) : (
					<span style={{ color: '#22c55e' }}>Бесплатно</span>
				)}
			</span>

			{/* Якорь для индикатора состояния: полоска-подчёркивание должна
			    висеть ПОД логотипом, а сама .model-icon режет выходящих за
			    края детей (overflow: hidden). */}
			<span className="model-icon-anchor">
				<span className="model-icon">
					<ModelIcon icon={model.icon} name={model.name} size={20} />
				</span>
				{!isTool && <HealthDot status={status} color={healthUi.color} label={healthTitle} />}
			</span>

			<div className="model-content">
				<div className="model-info">
					<span className="model-item-name">{model.name}</span>
					<span className="model-item-desc">{model.description}</span>
				</div>

				<div className="model-statistic">
					{isTool ? (
						<span className="model-tool-badge">Инструмент</span>
					) : (
						<>
							<span className="model-statistic_user">
								<User className="model-user" size={10} /> {model.users?.toLocaleString('ru-RU') || '—'}
							</span>

							<span>
								<Star size={12} /> {model.rating?.toFixed(2).replace('.', ',') || '—'}
							</span>

							{/* Щит ограничений — возле оценки (решение владельца).
							    Ключ бакета adapter+model_id; у моделей селекта
							    адаптер лежит в `provider`. */}
							<ModelShields adapter={model.adapter || model.provider} modelId={model.id} size={13} />
						</>
					)}
				</div>
			</div>

			{showRadio && <span className={`model-radio ${isActive ? 'checked' : ''}`} />}
		</button>
	)
}
